/**
 * Beneficial Owner Notices (Section 60C) — M32108
 *
 * 문서별 싸인대상(=수신·회신 대상) — 하단 [[Signers]] 에도 동일 인물 반영:
 *   BO01 법인주주 / BO02 개인주주 / BO03 의심 BO / BO04 정보보유자 / BO05·06 등록 BO
 * 하단 회사 Director/Secretary 추측 채움 없음.
 */
export type BoNoticeFieldType = 'text' | 'date' | 'entity' | 'person' | 'address';

export type BoNoticeDocCode =
  | 'BO01'
  | 'BO02'
  | 'BO03'
  | 'BO04'
  | 'BO05'
  | 'BO06';

export type BoNoticeRecipientMode =
  | 'corporate_member'
  | 'individual_member'
  | 'suspected_bo'
  | 'info_holder'
  | 'registered_bo';

export interface BoNoticeDocMeta {
  docCode: BoNoticeDocCode;
  label: string;
  stage: number;
  icon: string;
  keys: string[];
  recipientMode: BoNoticeRecipientMode;
  /** 싸인대상 designation 표시/저장 키 */
  designationKey: string;
  targetTitle: string;
}

const COMPANY = ['company_name', 'company_registration_no', 'registered_office_address'];
const CORP_MEMBER = ['corporate_member_name', 'corporate_member_address'];
const IND_MEMBER = ['member_name', 'member_address'];
const BO_RECIPIENT = ['bo_name', 'bo_address'];
const INFO_HOLDER = ['info_holder_name', 'info_holder_address'];
const COMMON_BASE = [...COMPANY, 'notice_date'];

export const BO_NOTICE_DOCS: Record<BoNoticeDocCode, BoNoticeDocMeta> = {
  BO01: {
    docCode: 'BO01',
    label: 'Notice Section 60C(1) - Corporate',
    stage: 1, icon: '🏢',
    recipientMode: 'corporate_member',
    designationKey: 'signer_designation_BO01',
    targetTitle: 'Corporate Member',
    keys: [...COMMON_BASE, ...CORP_MEMBER, 'signer_designation_BO01'],
  },
  BO02: {
    docCode: 'BO02',
    label: 'Notice Section 60C(1) - Individual',
    stage: 1, icon: '🙍',
    recipientMode: 'individual_member',
    designationKey: 'signer_designation_BO02',
    targetTitle: 'Individual Member',
    keys: [...COMMON_BASE, ...IND_MEMBER, 'signer_designation_BO02'],
  },
  BO03: {
    docCode: 'BO03',
    label: 'Notice Section 60C(2) - Identified BO',
    stage: 1, icon: '🔎',
    recipientMode: 'suspected_bo',
    designationKey: 'signer_designation_BO03',
    targetTitle: 'Beneficial Owner',
    keys: [...COMMON_BASE, ...BO_RECIPIENT, 'signer_designation_BO03'],
  },
  BO04: {
    docCode: 'BO04',
    label: 'Notice Section 60C(3)',
    stage: 1, icon: '📄',
    recipientMode: 'info_holder',
    designationKey: 'signer_designation_BO04',
    targetTitle: 'Information Holder',
    keys: [...COMMON_BASE, ...INFO_HOLDER, 'signer_designation_BO04'],
  },
  BO05: {
    docCode: 'BO05',
    label: 'Notice Section 60C(5) - Change of Particulars',
    stage: 2, icon: '✏️',
    recipientMode: 'registered_bo',
    designationKey: 'signer_designation_BO05',
    targetTitle: 'Beneficial Owner',
    keys: [...COMMON_BASE, ...BO_RECIPIENT, 'signer_designation_BO05'],
  },
  BO06: {
    docCode: 'BO06',
    label: 'Notice Section 60C(5) - Confirm Particulars',
    stage: 2, icon: '✅',
    recipientMode: 'registered_bo',
    designationKey: 'signer_designation_BO06',
    targetTitle: 'Beneficial Owner',
    keys: [...COMMON_BASE, ...BO_RECIPIENT, 'relevant_particulars', 'signer_designation_BO06'],
  },
};

export const BO_NOTICE_DOC_CODES = Object.keys(BO_NOTICE_DOCS) as BoNoticeDocCode[];

export const BO_NOTICE_STAGES: Array<{ no: number; en: string }> = [
  { no: 1, en: 'Initial Identification Notices (s.60C(1)–(3))' },
  { no: 2, en: 'Particulars Maintenance Notices (s.60C(5))' },
];

const OVERRIDES: Record<string, string> = {
  company_name: 'Company Name',
  company_registration_no: 'Company Registration No.',
  registered_office_address: 'Registered Office Address',
  notice_date: 'Notice Date',
  corporate_member_name: 'Corporate Member Name',
  corporate_member_address: 'Corporate Member Address',
  member_name: 'Member Name',
  member_address: 'Member Address',
  bo_name: 'Beneficial Owner Name',
  bo_address: 'Beneficial Owner Address',
  info_holder_name: 'Recipient Name',
  info_holder_address: 'Recipient Address',
  signer_designation: 'Signer Designation',
  signer_designation_BO01: 'Signer Designation',
  signer_designation_BO02: 'Signer Designation',
  signer_designation_BO03: 'Signer Designation',
  signer_designation_BO04: 'Signer Designation',
  signer_designation_BO05: 'Signer Designation',
  signer_designation_BO06: 'Signer Designation',
  relevant_particulars: 'Relevant Particulars',
};

export function labelize(key: string): string {
  if (OVERRIDES[key]) return OVERRIDES[key];
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function typeOf(key: string): BoNoticeFieldType {
  if (/_date$|^notice_date$/.test(key)) return 'date';
  if (/_address$|registered_office_address/.test(key)) return 'address';
  if (key === 'company_name' || key === 'company_registration_no') return 'entity';
  if (/^(corporate_member_name|member_name|bo_name|info_holder_name)$/.test(key)) return 'person';
  return 'text';
}

export function isOptional(key: string): boolean {
  return key === 'relevant_particulars'
    || key.startsWith('signer_designation')
    || key === 'bo_address'
    || key === 'info_holder_address'
    || key === 'member_address'
    || key === 'corporate_member_address';
}

/** Word [[member_*]] / [[signer_designation]] / [[bo_*]] 호환 매핑. */
export function adaptFormDataForDoc(
  docCode: BoNoticeDocCode,
  base: Record<string, any>,
): Record<string, any> {
  const fd = { ...base };
  const meta = BO_NOTICE_DOCS[docCode];
  const mode = meta.recipientMode;

  if (mode === 'corporate_member') {
    if (fd.corporate_member_name) fd.member_name = fd.corporate_member_name;
    if (fd.corporate_member_address) fd.member_address = fd.corporate_member_address;
  } else if (mode === 'suspected_bo' || mode === 'registered_bo') {
    if (fd.bo_name) fd.member_name = fd.bo_name;
    if (fd.bo_address) fd.member_address = fd.bo_address;
  } else if (mode === 'info_holder') {
    if (fd.info_holder_name) fd.member_name = fd.info_holder_name;
    if (fd.info_holder_address) fd.member_address = fd.info_holder_address;
  }

  const desig = fd[meta.designationKey] || meta.targetTitle;
  if (desig) fd.signer_designation = desig;

  return fd;
}
